import { useCallback, useEffect, useRef, useState } from 'react';
import type { ImperativePanelHandle } from '@ideoz/ui';

export const PROTOTYPE_PANEL_DEFAULT_PERCENT = 75;
export const CONVERSATION_PANEL_DEFAULT_PERCENT = 25;

/**
 * Owns the right-hand split-panel layout for the conversation page: whether
 * the prototype panel is open, the conversation/prototype resize handles, the
 * width-animation effect, and the "close on switch back to clarify" effect.
 *
 * `setIsOpen` is exposed because the streaming-orchestration effect (still in
 * the container) opens the panel imperatively when a prototype build starts.
 *
 * @param secondaryAlsoOpen   the streamed-artifact panel state (shares the slot)
 * @param currentMode         current conversation mode
 * @param initiatedFromUncover user explicitly asked to generate from clarify
 * @param scrollToBottom      scroll helper (run after layout settles)
 * @param setSidebarCollapsed collapse the left nav when the panel opens
 */
export function usePrototypePanel(params: {
  secondaryAlsoOpen: boolean;
  currentMode: string;
  initiatedFromUncover: boolean | undefined;
  scrollToBottom: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
}) {
  const { secondaryAlsoOpen, currentMode, initiatedFromUncover, scrollToBottom, setSidebarCollapsed } =
    params;

  const [isOpen, setIsOpen] = useState(false);
  const [isConversationPanelCollapsed, setIsConversationPanelCollapsed] = useState(false);
  const conversationPanelRef = useRef<ImperativePanelHandle | null>(null);
  const prototypePanelRef = useRef<ImperativePanelHandle | null>(null);
  const prevModeRef = useRef(currentMode);

  /** Conversation + streamed artifact (web) vs conversation + prototyper. */
  const isSecondaryOpen = isOpen || secondaryAlsoOpen;

  /**
   * Close the prototype panel when the user actively switches *back* to clarify
   * from another mode (unless the generation was initiated from clarify).
   *
   * We only act on a genuine mode *transition* into clarify — NOT whenever the
   * panel opens while already in clarify. The auto-open streaming flow (and the
   * "View prototype" reopen) opens the panel while the conversation is still in
   * clarify; a plain `currentMode === 'clarify' && isOpen` check would close it
   * right back (panel flashes open then disappears).
   */
  useEffect(() => {
    const prevMode = prevModeRef.current;
    prevModeRef.current = currentMode;
    if (
      prevMode !== 'clarify' &&
      currentMode === 'clarify' &&
      isOpen &&
      !initiatedFromUncover
    ) {
      setIsOpen(false);
    }
  }, [currentMode, isOpen, initiatedFromUncover]);

  /**
   * Smoothly animate panel widths when toggling prototype / artifact.
   * (FindingsPanel is a floating overlay now — no resize contribution.)
   */
  useEffect(() => {
    const prototypePanel = prototypePanelRef.current;
    const conversationPanel = conversationPanelRef.current;
    if (!prototypePanel || !conversationPanel) {
      return;
    }

    if (isSecondaryOpen) {
      prototypePanel.expand();
      prototypePanel.resize(PROTOTYPE_PANEL_DEFAULT_PERCENT);
      if (!conversationPanel.isCollapsed()) {
        conversationPanel.expand();
        conversationPanel.resize(CONVERSATION_PANEL_DEFAULT_PERCENT);
      }
      // Collapse the left sidebar so the prototype iframe gets the full width.
      setSidebarCollapsed(true);
    } else {
      prototypePanel.collapse();
      conversationPanel.expand();
      conversationPanel.resize(100);
    }

    // Scroll to bottom after panel layout settles
    setTimeout(() => {
      scrollToBottom();
    }, 600);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSecondaryOpen]);

  const close = useCallback(() => setIsOpen(false), []);
  const reopen = useCallback(() => setIsOpen(true), []);

  /** Toggle the conversation column collapsed/expanded (prototype stays open). */
  const toggleConversationPanel = useCallback(() => {
    const conversationPanel = conversationPanelRef.current;
    const prototypePanel = prototypePanelRef.current;
    if (!conversationPanel || !prototypePanel) return;

    if (conversationPanel.isCollapsed()) {
      conversationPanel.expand();
      // Ensure expand completes before resizing.
      setTimeout(() => {
        conversationPanel.resize(CONVERSATION_PANEL_DEFAULT_PERCENT);
        prototypePanel.resize(PROTOTYPE_PANEL_DEFAULT_PERCENT);
      }, 0);
    } else {
      conversationPanel.collapse();
    }
    setTimeout(() => {
      scrollToBottom();
    }, 400);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    isOpen,
    setIsOpen,
    isSecondaryOpen,
    isConversationPanelCollapsed,
    setIsConversationPanelCollapsed,
    conversationPanelRef,
    prototypePanelRef,
    close,
    reopen,
    toggleConversationPanel,
  };
}
